const DB_NAME = 'ErrorLogsDB';
const STORE_NAME = 'error_406_logs';
const MAX_RECORDS = 10;

let writeQueue = Promise.resolve();
let queueLength = 0;
const MAX_QUEUE_LENGTH = 100;

// ------------------ OPEN DB ------------------
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = event => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { autoIncrement: true });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// ------------------ CLEAR DB (DATE CHANGE) ------------------
const clearAllRecords = db => {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.clear();

    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
};

const handleDateRollover = async db => {
  const today = new Date().toLocaleDateString('en-CA');

  const data = await new Promise((res, rej) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();

    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });

  // If no logs → nothing to clear
  if (!data.length) return;

  // Get latest record (last inserted)
  const latestRecord = data[data.length - 1];

  const storedDate = new Date(latestRecord.storedAt).toLocaleDateString(
    'en-CA'
  );

  if (storedDate !== today) {
    await clearAllRecords(db);
  }
};

// ------------------ HELPERS ------------------
const getAllKeys = db => {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAllKeys();

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

const deleteRecordsByKeys = (db, keys) => {
  return new Promise((resolve, reject) => {
    if (!keys.length) return resolve();

    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    let done = 0;
    keys.forEach(key => {
      const req = store.delete(key);
      req.onsuccess = () => {
        done++;
        if (done === keys.length) resolve();
      };
      req.onerror = () => reject(req.error);
    });
  });
};

// ------------------ ADD RECORD ------------------
const addRecord = (db, payload) => {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    const safePayload = {
      ...payload,
      time: new Date().toISOString(), // ✅ UTC
    };

    const req = store.add({
      data: safePayload,
      storedAt: new Date().toISOString(),
    });

    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
};

// ------------------ WRITE FLOW ------------------
const performWrite = async payload => {
  const db = await openDB();
  await handleDateRollover(db);

  const keys = await getAllKeys(db);
  const overflow = keys.length + 1 - MAX_RECORDS;

  if (overflow > 0) {
    await deleteRecordsByKeys(db, keys.slice(0, overflow));
  }

  await addRecord(db, payload);
};

// ------------------ PUBLIC: STORE LOG ------------------
export const storeError406Log = payload => {
  if (queueLength >= MAX_QUEUE_LENGTH) return;

  queueLength++;

  writeQueue = writeQueue
    .then(() => performWrite(payload))
    .catch(err => console.error('IndexedDB error:', err))
    .finally(() => queueLength--);

  return writeQueue;
};

// ------------------ COPY LOGS ------------------
export const copyLogs = async () => {
  const db = await openDB();

  const data = await new Promise((res, rej) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();

    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });

  const parsed = data.map(item => item.data);
  const json = JSON.stringify(parsed, null, 2);

  await navigator.clipboard.writeText(json);
};

// ------------------ DOWNLOAD LOGS ------------------
export const downloadLogs = async () => {
  const db = await openDB();

  const data = await new Promise((res, rej) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.getAll();

    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });

  const parsed = data.map(item => item.data);

  const blob = new Blob([JSON.stringify(parsed, null, 2)], {
    type: 'application/json',
  });

  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'error-logs.json';
  a.click();
};
